import { useEffect, useRef, useCallback, useState } from 'react';
import SEO from '@/components/SEO';
import { Link } from 'react-router-dom';
import { ArrowRight, Volume2, VolumeX } from 'lucide-react';

const GOLD = '#C8A840';
const T = 74000;

/* ── Cinematic images for Ken Burns backgrounds ── */
const SCENE_IMAGES = [
  'https://images.unsplash.com/photo-1602685235383-5173580c6026?w=1920&q=80',
  'https://images.unsplash.com/photo-1761005639407-da9aa4caae12?w=1920&q=80',
  'https://images.unsplash.com/photo-1642521684111-d5464d8fef88?w=1920&q=80',
  'https://images.pexels.com/photos/33664025/pexels-photo-33664025.jpeg?auto=compress&cs=tinysrgb&w=1920',
  'https://images.unsplash.com/photo-1630083130543-3bacb9f9af57?w=1920&q=80',
  'https://images.pexels.com/photos/9182268/pexels-photo-9182268.jpeg?auto=compress&cs=tinysrgb&w=1920',
  'https://images.unsplash.com/photo-1642899612887-89747dbdb160?w=1920&q=80',
  'https://images.unsplash.com/photo-1679579664878-2c025e3f87b0?w=1920&q=80',
];

/* ── Image scene timing: [imageIndex, startMs, endMs] ── */
const SCENE_TIMELINE = [
  [0, 0, 5000],
  [1, 5000, 10500],
  [2, 10500, 16000],
  [3, 16000, 22000],
  [4, 22000, 28500],
  [5, 28500, 36000],
  [6, 36000, 43500],
  [7, 43500, 50500],
  [0, 50500, 57500],
  [4, 57500, 63000],
  [1, 63000, 68000],
  [6, 68000, 74000],
];

/* ── Text beats: [id, startMs, endMs] ── */
const BEATS = [
  ['a', 200, 4800],
  ['ba', 5200, 10000], ['bb', 6000, 10000], ['bc', 7200, 10000],
  ['ca', 10500, 15500], ['cb', 11500, 15500], ['cc', 13000, 15500],
  ['da', 16000, 21500], ['db', 17000, 21500], ['dc', 18500, 21500],
  ['ea', 22000, 28000], ['eb', 22700, 28000], ['ec', 24800, 28000], ['ed', 26000, 28000],
  ['fa', 28500, 35500], ['fb', 29300, 35500], ['fc', 31500, 35500],
  ['ga', 36000, 43000], ['gb', 37000, 43000], ['gc', 39500, 43000],
  ['ha', 43500, 50000], ['hb', 44500, 50000], ['hc', 47000, 50000],
  ['ia', 50500, 57000], ['ib', 51200, 57000],
  ['ka', 57500, 62500], ['kb', 58200, 62500], ['kc', 59500, 62500],
  ['kd', 63000, 67500], ['ke', 64500, 67500],
  ['la', 68000, 74000], ['lb', 69000, 74000], ['lc', 70000, 74000], ['ld', 71000, 74000],
];

/* ── Light leak flash timing (ms) ── */
const FLASHES = [200, 5200, 10500, 16000, 22000, 28500, 36000, 43500, 50500, 57500, 63000, 68000];

/* ═══════════════════════════════════════════════════
   SOUND ENGINE — Web Audio API cinematic atmosphere
   ═══════════════════════════════════════════════════ */
class CinematicAudio {
  constructor() {
    this.ctx = null;
    this.masterGain = null;
    this.droneGain = null;
    this.isPlaying = false;
    this.nodes = [];
  }

  init() {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') this.ctx.resume();
      return;
    }
    this.ctx = new (window.AudioContext || window.webkitAudioContext)();
    if (this.ctx.state === 'suspended') this.ctx.resume();
    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = 0;
    this.masterGain.connect(this.ctx.destination);
  }

  fadeIn(duration = 3) {
    if (!this.ctx) return;
    this.masterGain.gain.cancelScheduledValues(this.ctx.currentTime);
    this.masterGain.gain.setValueAtTime(this.masterGain.gain.value, this.ctx.currentTime);
    this.masterGain.gain.linearRampToValueAtTime(1.0, this.ctx.currentTime + duration);
  }

  fadeOut(duration = 2) {
    if (!this.ctx) return;
    this.masterGain.gain.cancelScheduledValues(this.ctx.currentTime);
    this.masterGain.gain.setValueAtTime(this.masterGain.gain.value, this.ctx.currentTime);
    this.masterGain.gain.linearRampToValueAtTime(0, this.ctx.currentTime + duration);
  }

  startDrone() {
    if (!this.ctx || this.isPlaying) return;
    if (this.ctx.state === 'suspended') this.ctx.resume();
    this.isPlaying = true;

    // Low foundation (audible on most speakers)
    const osc1 = this.ctx.createOscillator();
    osc1.type = 'sine';
    osc1.frequency.value = 110;
    const g1 = this.ctx.createGain();
    g1.gain.value = 0.18;
    osc1.connect(g1);
    g1.connect(this.masterGain);
    osc1.start();
    this.nodes.push(osc1);

    // Warm mid pad (clearly audible)
    const osc2 = this.ctx.createOscillator();
    osc2.type = 'triangle';
    osc2.frequency.value = 220;
    const g2 = this.ctx.createGain();
    g2.gain.value = 0.12;
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 600;
    osc2.connect(filter);
    filter.connect(g2);
    g2.connect(this.masterGain);
    osc2.start();
    this.nodes.push(osc2);

    // Ethereal high pad (adds shimmer)
    const osc3 = this.ctx.createOscillator();
    osc3.type = 'sine';
    osc3.frequency.value = 330;
    const g3 = this.ctx.createGain();
    g3.gain.value = 0.06;
    osc3.connect(g3);
    g3.connect(this.masterGain);
    osc3.start();
    this.nodes.push(osc3);

    // Detuned fifth for depth
    const osc4 = this.ctx.createOscillator();
    osc4.type = 'sine';
    osc4.frequency.value = 165;
    const g4 = this.ctx.createGain();
    g4.gain.value = 0.08;
    const f4 = this.ctx.createBiquadFilter();
    f4.type = 'lowpass';
    f4.frequency.value = 500;
    osc4.connect(f4);
    f4.connect(g4);
    g4.connect(this.masterGain);
    osc4.start();
    this.nodes.push(osc4);

    // Breathy noise layer (white noise filtered)
    const bufSize = this.ctx.sampleRate * 2;
    const noiseBuf = this.ctx.createBuffer(1, bufSize, this.ctx.sampleRate);
    const data = noiseBuf.getChannelData(0);
    for (let i = 0; i < bufSize; i++) data[i] = Math.random() * 2 - 1;
    const noise = this.ctx.createBufferSource();
    noise.buffer = noiseBuf;
    noise.loop = true;
    const noiseFilter = this.ctx.createBiquadFilter();
    noiseFilter.type = 'bandpass';
    noiseFilter.frequency.value = 400;
    noiseFilter.Q.value = 0.5;
    const noiseGain = this.ctx.createGain();
    noiseGain.gain.value = 0.04;
    noise.connect(noiseFilter);
    noiseFilter.connect(noiseGain);
    noiseGain.connect(this.masterGain);
    noise.start();
    this.nodes.push(noise);

    // Slow LFO pulsing on the main pad
    const lfo = this.ctx.createOscillator();
    lfo.type = 'sine';
    lfo.frequency.value = 0.12;
    const lfoGain = this.ctx.createGain();
    lfoGain.gain.value = 0.04;
    lfo.connect(lfoGain);
    lfoGain.connect(g1.gain);
    lfo.start();
    this.nodes.push(lfo);

    this.fadeIn(3);
  }

  playHit(frequency = 330, duration = 3) {
    if (!this.ctx) return;
    if (this.ctx.state === 'suspended') this.ctx.resume();
    // Main tone
    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = frequency;
    const env = this.ctx.createGain();
    env.gain.setValueAtTime(0, this.ctx.currentTime);
    env.gain.linearRampToValueAtTime(0.25, this.ctx.currentTime + 0.08);
    env.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + duration);
    osc.connect(env);
    env.connect(this.masterGain);
    osc.start();
    osc.stop(this.ctx.currentTime + duration);
    // Octave harmonic
    const osc2 = this.ctx.createOscillator();
    osc2.type = 'sine';
    osc2.frequency.value = frequency * 2;
    const env2 = this.ctx.createGain();
    env2.gain.setValueAtTime(0, this.ctx.currentTime);
    env2.gain.linearRampToValueAtTime(0.08, this.ctx.currentTime + 0.05);
    env2.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + duration * 0.6);
    osc2.connect(env2);
    env2.connect(this.masterGain);
    osc2.start();
    osc2.stop(this.ctx.currentTime + duration);
  }

  playBassHit() {
    if (!this.ctx) return;
    if (this.ctx.state === 'suspended') this.ctx.resume();
    // Deep sweep
    const osc = this.ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(150, this.ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(60, this.ctx.currentTime + 1.5);
    const env = this.ctx.createGain();
    env.gain.setValueAtTime(0, this.ctx.currentTime);
    env.gain.linearRampToValueAtTime(0.5, this.ctx.currentTime + 0.03);
    env.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + 2.5);
    osc.connect(env);
    env.connect(this.masterGain);
    osc.start();
    osc.stop(this.ctx.currentTime + 3);
    // Impact noise burst
    const bufSize = this.ctx.sampleRate;
    const buf = this.ctx.createBuffer(1, bufSize, this.ctx.sampleRate);
    const d = buf.getChannelData(0);
    for (let i = 0; i < bufSize; i++) d[i] = Math.random() * 2 - 1;
    const noise = this.ctx.createBufferSource();
    noise.buffer = buf;
    const nf = this.ctx.createBiquadFilter();
    nf.type = 'lowpass';
    nf.frequency.value = 300;
    const ne = this.ctx.createGain();
    ne.gain.setValueAtTime(0.3, this.ctx.currentTime);
    ne.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + 0.5);
    noise.connect(nf);
    nf.connect(ne);
    ne.connect(this.masterGain);
    noise.start();
    noise.stop(this.ctx.currentTime + 0.6);
  }

  playHeartbeat() {
    if (!this.ctx) return;
    if (this.ctx.state === 'suspended') this.ctx.resume();
    const play = (delay) => {
      // Use 100Hz which laptops can reproduce
      const osc = this.ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = 100;
      const env = this.ctx.createGain();
      env.gain.setValueAtTime(0, this.ctx.currentTime + delay);
      env.gain.linearRampToValueAtTime(0.3, this.ctx.currentTime + delay + 0.03);
      env.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + delay + 0.35);
      osc.connect(env);
      env.connect(this.masterGain);
      osc.start(this.ctx.currentTime + delay);
      osc.stop(this.ctx.currentTime + delay + 0.5);
      // Click layer
      const osc2 = this.ctx.createOscillator();
      osc2.type = 'triangle';
      osc2.frequency.value = 200;
      const env2 = this.ctx.createGain();
      env2.gain.setValueAtTime(0, this.ctx.currentTime + delay);
      env2.gain.linearRampToValueAtTime(0.12, this.ctx.currentTime + delay + 0.01);
      env2.gain.exponentialRampToValueAtTime(0.001, this.ctx.currentTime + delay + 0.15);
      osc2.connect(env2);
      env2.connect(this.masterGain);
      osc2.start(this.ctx.currentTime + delay);
      osc2.stop(this.ctx.currentTime + delay + 0.2);
    };
    play(0);
    play(0.22);
  }

  stop() {
    this.fadeOut(1);
    setTimeout(() => {
      this.nodes.forEach(n => { try { n.stop(); } catch(e) {} });
      this.nodes = [];
      this.isPlaying = false;
    }, 1200);
  }

  destroy() {
    this.stop();
    setTimeout(() => {
      if (this.ctx) { this.ctx.close(); this.ctx = null; }
    }, 1500);
  }
}

/* ═══════════════════════════════════════════════════
   DUST PARTICLES — Canvas-based floating specks
   ═══════════════════════════════════════════════════ */
const DustCanvas = ({ active }) => {
  const canvasRef = useRef(null);
  const particles = useRef([]);
  const rafRef = useRef(null);

  useEffect(() => {
    if (!active) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width = window.innerWidth;
    const H = canvas.height = window.innerHeight;

    particles.current = Array.from({ length: 60 }, () => ({
      x: Math.random() * W,
      y: Math.random() * H,
      r: Math.random() * 1.5 + 0.3,
      dx: (Math.random() - 0.5) * 0.3,
      dy: Math.random() * -0.4 - 0.1,
      o: Math.random() * 0.4 + 0.1,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      for (const p of particles.current) {
        p.x += p.dx;
        p.y += p.dy;
        if (p.y < -10) { p.y = H + 10; p.x = Math.random() * W; }
        if (p.x < -10) p.x = W + 10;
        if (p.x > W + 10) p.x = -10;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(200, 168, 64, ${p.o})`;
        ctx.fill();
      }
      rafRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [active]);

  if (!active) return null;
  return <canvas ref={canvasRef} style={{ position: 'absolute', inset: 0, zIndex: 15, pointerEvents: 'none' }} />;
};

/* ═══════════════════════════════════════════════════
   CINEMATIC TEASER — Full-screen timeline experience
   ═══════════════════════════════════════════════════ */
const CinematicTeaser = ({ soundEnabled, audioRef }) => {
  const cinemaRef = useRef(null);
  const t0Ref = useRef(null);
  const rfRef = useRef(null);
  const lastSceneRef = useRef(-1);
  const firedFlashes = useRef(new Set());
  const firedHits = useRef(new Set());

  const tick = useCallback((ts) => {
    if (!t0Ref.current) t0Ref.current = ts;
    const t = ts - t0Ref.current;
    const p = Math.min(t / T, 1);
    const cinema = cinemaRef.current;
    if (!cinema) return;

    /* Progress bars */
    const prog = cinema.querySelector('[data-el="prog"]');
    const vbar = cinema.querySelector('[data-el="vbar"]');
    const tmr = cinema.querySelector('[data-el="tmr"]');
    const rp = cinema.querySelector('[data-el="rp"]');
    if (prog) prog.style.width = (p * 100) + '%';
    if (vbar) vbar.style.height = (p * 100) + '%';
    const s = Math.floor(t / 1000);
    if (tmr) tmr.textContent = `${Math.floor(s/60)}:${String(s%60).padStart(2,'0')}`;

    /* Scene images — Ken Burns crossfade */
    for (const [imgIdx, s0, s1] of SCENE_TIMELINE) {
      const sceneEl = cinema.querySelector(`[data-scene="${imgIdx}"]`);
      if (!sceneEl) continue;
      if (t >= s0 && t < s1) {
        const fi = Math.min((t - s0) / 1500, 1);
        const fo = s1 - t < 1200 ? (s1 - t) / 1200 : 1;
        sceneEl.style.opacity = Math.min(fi, fo) * 0.35;
        const progress = (t - s0) / (s1 - s0);
        const scale = 1.0 + progress * 0.08;
        const tx = (imgIdx % 2 === 0 ? progress : -progress) * 3;
        sceneEl.style.transform = `scale(${scale}) translateX(${tx}%)`;
      } else {
        sceneEl.style.opacity = 0;
      }
    }

    /* Fire scene-change sound hits */
    if (soundEnabled && audioRef.current) {
      for (const [, s0] of SCENE_TIMELINE) {
        if (t >= s0 && t < s0 + 100 && !firedHits.current.has(s0)) {
          firedHits.current.add(s0);
          const notes = [440, 392, 349, 330, 294, 262, 440, 392, 349, 330, 294, 262];
          const idx = SCENE_TIMELINE.findIndex(st => st[1] === s0);
          audioRef.current.playHit(notes[idx % notes.length], 3);
        }
      }
      // Heartbeats during the question section
      if (t >= 50500 && t < 57000) {
        const hbInterval = 1200;
        const hbKey = Math.floor((t - 50500) / hbInterval);
        if (!firedHits.current.has(`hb_${hbKey}`)) {
          firedHits.current.add(`hb_${hbKey}`);
          audioRef.current.playHeartbeat();
        }
      }
      // Bass hit on title reveal
      if (t >= 68000 && !firedHits.current.has('bass')) {
        firedHits.current.add('bass');
        audioRef.current.playBassHit();
      }
    }

    /* Light leak flash */
    const flashEl = cinema.querySelector('[data-el="flash"]');
    if (flashEl) {
      let flashOpacity = 0;
      for (const ft of FLASHES) {
        if (t >= ft && t < ft + 400) {
          const prog = (t - ft) / 400;
          flashOpacity = Math.max(flashOpacity, prog < 0.15 ? prog / 0.15 * 0.25 : 0.25 * (1 - (prog - 0.15) / 0.85));
        }
      }
      flashEl.style.opacity = flashOpacity;
    }

    /* Vignette pulse */
    const vigEl = cinema.querySelector('[data-el="vig"]');
    if (vigEl) {
      const vp = 0.7 + Math.sin(t / 3000) * 0.05;
      vigEl.style.opacity = vp;
    }

    /* Text beats */
    for (const [id, s0, s1] of BEATS) {
      const el = cinema.querySelector(`[data-beat="${id}"]`);
      if (!el) continue;
      if (t >= s0 && t < s1) {
        const fi = Math.min((t - s0) / 900, 1);
        const fo = s1 - t < 800 ? (s1 - t) / 800 : 1;
        const o = Math.min(fi, fo);
        el.style.opacity = o;
        const slideUp = (1 - Math.min((t - s0) / 1200, 1)) * 15;
        el.style.transform = `translateY(${slideUp}px)`;
      } else {
        el.style.opacity = 0;
        el.style.transform = 'translateY(15px)';
      }
    }

    if (p < 1) {
      rfRef.current = requestAnimationFrame(tick);
    } else {
      if (rp) rp.style.opacity = 1;
      if (soundEnabled && audioRef.current) audioRef.current.fadeOut(3);
    }
  }, [soundEnabled, audioRef]);

  const restart = useCallback(() => {
    if (rfRef.current) cancelAnimationFrame(rfRef.current);
    t0Ref.current = null;
    lastSceneRef.current = -1;
    firedFlashes.current.clear();
    firedHits.current.clear();

    const cinema = cinemaRef.current;
    if (!cinema) return;

    const prog = cinema.querySelector('[data-el="prog"]');
    const vbar = cinema.querySelector('[data-el="vbar"]');
    const rp = cinema.querySelector('[data-el="rp"]');
    const tmr = cinema.querySelector('[data-el="tmr"]');
    if (prog) prog.style.width = '0%';
    if (vbar) vbar.style.height = '0%';
    if (rp) rp.style.opacity = 0;
    if (tmr) tmr.textContent = '0:00';
    cinema.querySelectorAll('[data-beat]').forEach(el => { el.style.opacity = 0; });
    cinema.querySelectorAll('[data-scene]').forEach(el => { el.style.opacity = 0; });

    if (soundEnabled && audioRef.current) {
      audioRef.current.init();
      audioRef.current.startDrone();
    }

    rfRef.current = requestAnimationFrame(tick);
  }, [tick, soundEnabled, audioRef]);

  useEffect(() => {
    restart();
    return () => { if (rfRef.current) cancelAnimationFrame(rfRef.current); };
  }, [restart]);

  const ln = { position: 'absolute', opacity: 0, textAlign: 'center', width: '88%', left: '6%', transition: 'transform 0.3s ease-out' };

  return (
    <div
      ref={cinemaRef}
      data-testid="film-teaser-cinema"
      onClick={restart}
      className="relative w-full cursor-pointer overflow-hidden mx-auto"
      style={{ maxWidth: '100%', height: '100vh', background: '#000' }}
    >
      {/* Ken Burns background images */}
      {SCENE_IMAGES.map((src, i) => (
        <div
          key={i}
          data-scene={i}
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${src})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0,
            transition: 'opacity 0.3s',
            willChange: 'transform, opacity',
          }}
        />
      ))}

      {/* Film grain overlay */}
      <div className="absolute inset-0 z-10 pointer-events-none film-grain-overlay" />

      {/* Vignette */}
      <div
        data-el="vig"
        className="absolute inset-0 z-12 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 40%, rgba(0,0,0,0.85) 100%)',
          opacity: 0.7,
        }}
      />

      {/* Light leak flash */}
      <div
        data-el="flash"
        className="absolute inset-0 z-13 pointer-events-none"
        style={{
          background: 'linear-gradient(135deg, rgba(200,168,64,0.4) 0%, transparent 50%, rgba(200,168,64,0.2) 100%)',
          opacity: 0,
          mixBlendMode: 'screen',
        }}
      />

      {/* Dust particles */}
      <DustCanvas active={true} />

      {/* Letterbox bars */}
      <div className="absolute top-0 left-0 right-0 z-20" style={{ height: '12vh', background: 'linear-gradient(to bottom, #000 70%, transparent)' }} />
      <div className="absolute bottom-0 left-0 right-0 z-20" style={{ height: '12vh', background: 'linear-gradient(to top, #000 70%, transparent)' }} />

      {/* Film label badges - top corners */}
      <div className="absolute top-[13vh] left-8 z-25 opacity-30" style={{ fontFamily: "'DM Mono', monospace", fontSize: '8px', letterSpacing: '0.2em', color: GOLD, textAlign: 'center' }}>
        <div>A FILM BY</div>
        <div>SHE BUILT THIS</div>
        <div>PRODUCTIONS</div>
      </div>
      <div className="absolute top-[13vh] right-8 z-25 opacity-30" style={{ fontFamily: "'DM Mono', monospace", fontSize: '8px', letterSpacing: '0.2em', color: GOLD, textAlign: 'center' }}>
        <div>IN</div>
        <div>DEVELOPMENT</div>
        <div>2026</div>
      </div>

      {/* Screen — text beats */}
      <div className="absolute inset-0 flex items-center justify-center z-16">

        {/* Beat 1 — DAWN */}
        <div data-beat="a" style={{ ...ln, top: '42%', fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(36px, 6vw, 72px)', fontWeight: 700, lineHeight: 1.3, letterSpacing: '0.12em', color: '#fff', textShadow: '0 0 60px rgba(200,168,64,0.3)' }}>DAWN.</div>

        {/* Beat 2 — DIRT ROAD */}
        <div data-beat="ba" style={{ ...ln, top: '30%', fontSize: '11px', letterSpacing: '0.35em', fontFamily: "'DM Mono', monospace", color: GOLD }}>A DIRT ROAD. A CONFLICT REGION. 2009.</div>
        <div data-beat="bb" style={{ ...ln, top: '44%', fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: 'clamp(18px, 2.5vw, 26px)', lineHeight: 1.85, letterSpacing: '0.04em', color: '#fff' }}>A woman drives alone to a construction site.</div>
        <div data-beat="bc" style={{ ...ln, top: '58%', fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: 'clamp(18px, 2.5vw, 26px)', lineHeight: 1.85, color: 'rgba(255,255,255,0.4)', fontStyle: 'italic' }}>She has made this drive every morning for seven months.</div>

        {/* Beat 3 — SHE STOPS */}
        <div data-beat="ca" style={{ ...ln, top: '34%', fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(24px, 3.5vw, 40px)', lineHeight: 1.6, letterSpacing: '0.04em', color: '#fff' }}>She stops.</div>
        <div data-beat="cb" style={{ ...ln, top: '48%', fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(24px, 3.5vw, 40px)', lineHeight: 1.6, color: GOLD, textShadow: '0 0 40px rgba(200,168,64,0.2)' }}>The wall has been damaged in the night.</div>
        <div data-beat="cc" style={{ ...ln, top: '64%', fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: 'clamp(18px, 2.5vw, 26px)', lineHeight: 1.85, color: 'rgba(255,255,255,0.35)', fontStyle: 'italic' }}>Someone needed the bricks more than she did.</div>

        {/* Beat 4 — NOTEBOOK */}
        <div data-beat="da" style={{ ...ln, top: '30%', fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: 'clamp(18px, 2.5vw, 26px)', lineHeight: 1.85, color: '#fff' }}>She opens a notebook.</div>
        <div data-beat="db" style={{ ...ln, top: '44%', fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: 'clamp(18px, 2.5vw, 26px)', lineHeight: 1.85, color: GOLD }}>She writes what is missing.</div>
        <div data-beat="dc" style={{ ...ln, top: '58%', fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: 'clamp(18px, 2.5vw, 26px)', lineHeight: 1.85, color: '#fff' }}>Three calls. The builders return at dawn.</div>

        {/* Beat 5 — SCHOOL */}
        <div data-beat="ea" style={{ ...ln, top: '22%', fontSize: '11px', letterSpacing: '0.35em', fontFamily: "'DM Mono', monospace", color: '#fff' }}>THE SCHOOL OPENED SIX MONTHS LATER.</div>
        <div data-beat="eb" style={{ ...ln, top: '34%', fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(50px, 8vw, 100px)', fontWeight: 700, lineHeight: 1, letterSpacing: '0.12em', color: GOLD, textShadow: '0 0 80px rgba(200,168,64,0.4)' }}>Still open.</div>
        <div data-beat="ec" style={{ ...ln, top: '62%', fontSize: '11px', letterSpacing: '0.35em', fontFamily: "'DM Mono', monospace", color: '#fff' }}>EVERY MORNING. FIFTEEN YEARS.</div>
        <div data-beat="ed" style={{ ...ln, top: '72%', fontSize: '11px', letterSpacing: '0.35em', fontFamily: "'DM Mono', monospace", color: 'rgba(255,255,255,0.25)' }}>HER NAME IS NOT ON THE BUILDING.</div>

        {/* Beat 6 — ZERO */}
        <div data-beat="fa" style={{ ...ln, top: '14%', fontSize: '11px', letterSpacing: '0.35em', fontFamily: "'DM Mono', monospace", color: '#fff' }}>50 YEARS. $8 BILLION A YEAR.</div>
        <div data-beat="fb" style={{ ...ln, top: '24%', fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(100px, 18vw, 200px)', fontWeight: 700, lineHeight: 1, color: '#fff', textShadow: '0 0 120px rgba(255,255,255,0.15)' }}>0</div>
        <div data-beat="fc" style={{ ...ln, top: '76%', fontSize: '11px', letterSpacing: '0.35em', fontFamily: "'DM Mono', monospace", color: GOLD }}>PROGRAMMES PROVEN TO PRODUCE THE OUTCOME THEY PROMISED.</div>

        {/* Beat 7 — FOLDER */}
        <div data-beat="ga" style={{ ...ln, top: '24%', fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(24px, 3.5vw, 40px)', lineHeight: 1.6, color: '#fff' }}>She placed a folder on the table.</div>
        <div data-beat="gb" style={{ ...ln, top: '40%', fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: 'clamp(18px, 2.5vw, 26px)', lineHeight: 2, color: '#fff' }}>Every receipt. Every permit.<br />Every registration. Every curriculum filing.</div>
        <div data-beat="gc" style={{ ...ln, top: '66%', fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: 'clamp(18px, 2.5vw, 26px)', lineHeight: 1.85, color: GOLD }}>Fifteen years of evidence.<br />Before anyone thought to ask.</div>

        {/* Beat 8 — LEFT AT 23 */}
        <div data-beat="ha" style={{ ...ln, top: '28%', fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(24px, 3.5vw, 40px)', lineHeight: 1.6, color: '#fff' }}>She left at twenty-three.</div>
        <div data-beat="hb" style={{ ...ln, top: '44%', fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 300, fontSize: 'clamp(18px, 2.5vw, 26px)', lineHeight: 2, color: '#fff' }}>Four years of credential recognition.<br />A bureaucracy not designed for her.</div>
        <div data-beat="hc" style={{ ...ln, top: '66%', fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(24px, 3.5vw, 40px)', lineHeight: 1.6, color: GOLD, textShadow: '0 0 40px rgba(200,168,64,0.2)' }}>She came back anyway.<br />Because building was the answer.</div>

        {/* Beat 9 — THE QUESTION */}
        <div data-beat="ia" style={{ ...ln, top: '18%', fontSize: '11px', letterSpacing: '0.35em', fontFamily: "'DM Mono', monospace", color: GOLD }}>THE CENTRAL QUESTION OF THIS FILM:</div>
        <div data-beat="ib" style={{ ...ln, top: '28%', fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(30px, 5vw, 60px)', fontWeight: 700, lineHeight: 1.5, letterSpacing: '0.04em', color: GOLD, textShadow: '0 0 80px rgba(200,168,64,0.3)' }}>What does it cost a person<br />to build something that will never<br />carry their name?</div>

        {/* Beat 10 — THE ANSWER */}
        <div data-beat="ka" style={{ ...ln, top: '20%', fontSize: '11px', letterSpacing: '0.35em', fontFamily: "'DM Mono', monospace", color: 'rgba(255,255,255,0.5)' }}>AMARA&nbsp;&nbsp;(O.C.)</div>
        <div data-beat="kb" style={{ ...ln, top: '32%', fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(36px, 5.5vw, 72px)', fontStyle: 'italic', lineHeight: 1.25, color: '#fff' }}>Would you do it again &mdash;</div>
        <div data-beat="kc" style={{ ...ln, top: '50%', fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(36px, 5.5vw, 72px)', fontStyle: 'italic', lineHeight: 1.25, color: GOLD, textShadow: '0 0 60px rgba(200,168,64,0.3)' }}>knowing no one<br />was going to look?</div>
        <div data-beat="kd" style={{ ...ln, top: '40%', fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(72px, 12vw, 140px)', fontWeight: 700, fontStyle: 'italic', color: '#fff', letterSpacing: '0.04em', textShadow: '0 0 100px rgba(255,255,255,0.2)' }}>Yes.</div>
        <div data-beat="ke" style={{ ...ln, top: '56%', fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(72px, 12vw, 140px)', fontWeight: 700, fontStyle: 'italic', color: GOLD, letterSpacing: '0.04em', textShadow: '0 0 100px rgba(200,168,64,0.4)' }}>I would.</div>

        {/* Beat 11 — TITLE */}
        <div data-beat="la" style={{ ...ln, top: '28%', fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(48px, 8vw, 100px)', fontWeight: 700, lineHeight: 1, letterSpacing: '0.18em', color: '#fff', textShadow: '0 0 120px rgba(255,255,255,0.2)' }}>SHE BUILT THIS</div>
        <div data-beat="lb" style={{ ...ln, top: '56%', fontSize: '11px', letterSpacing: '0.35em', fontFamily: "'DM Mono', monospace", color: GOLD }}>A FEATURE DOCUMENTARY &middot; IN DEVELOPMENT 2026</div>
        <div data-beat="lc" style={{ ...ln, top: '64%', fontSize: '11px', letterSpacing: '0.35em', fontFamily: "'DM Mono', monospace", color: 'rgba(255,255,255,0.5)' }}>VISIONMOVIE &middot; SHE BUILT THIS PRODUCTIONS</div>
        <div data-beat="ld" style={{ ...ln, top: '72%', fontSize: '11px', letterSpacing: '0.35em', fontFamily: "'DM Mono', monospace", color: 'rgba(255,255,255,0.2)' }}>SHEBUILTTHIS.CO</div>
      </div>

      {/* Vertical gold progress bar */}
      <div data-el="vbar" className="absolute top-0 left-0 z-25" style={{ width: '2px', height: '0%', background: `linear-gradient(to bottom, transparent, ${GOLD})` }} />

      {/* Bottom progress bar */}
      <div data-el="prog" className="absolute bottom-0 left-0 z-25" style={{ height: '2px', background: `linear-gradient(to right, ${GOLD}, rgba(200,168,64,0.3))`, width: '0%' }} />

      {/* Timer */}
      <div data-el="tmr" className="absolute z-25" style={{ top: '13vh', right: '22px', color: 'rgba(200,168,64,0.3)', fontSize: '10px', letterSpacing: '0.2em', fontFamily: "'DM Mono', monospace" }}>0:00</div>

      {/* Replay */}
      <div
        data-el="rp"
        data-testid="film-teaser-replay"
        onClick={(e) => { e.stopPropagation(); restart(); }}
        className="absolute z-30 cursor-pointer transition-colors duration-300"
        style={{ bottom: '13vh', right: '22px', color: 'rgba(200,168,64,0.4)', fontSize: '10px', letterSpacing: '0.25em', fontFamily: "'DM Mono', monospace", opacity: 0 }}
        onMouseEnter={(e) => e.currentTarget.style.color = GOLD}
        onMouseLeave={(e) => e.currentTarget.style.color = 'rgba(200,168,64,0.4)'}
      >
        &#8634; REPLAY
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   FILM PAGE — Complete cinematic experience
   ═══════════════════════════════════════════════════ */
const FilmPage = () => {
  const [entered, setEntered] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    audioRef.current = new CinematicAudio();
    return () => { if (audioRef.current) audioRef.current.destroy(); };
  }, []);

  const enterCinema = (withSound) => {
    setSoundEnabled(withSound);
    if (withSound && audioRef.current) {
      audioRef.current.init();
      audioRef.current.startDrone();
    }
    setEntered(true);
  };

  const toggleSound = () => {
    const next = !soundEnabled;
    setSoundEnabled(next);
    if (next && audioRef.current) {
      audioRef.current.init();
      audioRef.current.startDrone();
    } else if (audioRef.current) {
      audioRef.current.stop();
    }
  };

  return (
    <div data-testid="film-page" className="min-h-screen bg-black">
      <SEO
        title="The Film | She Built This"
        description="A feature documentary about women who build. In development 2026. Oscar-campaign teaser experience."
      />

      {/* ── CURTAIN / ENTRANCE ── */}
      {!entered && (
        <div className="fixed inset-0 z-50 bg-black flex items-center justify-center" style={{ paddingTop: '60px' }}>
          {/* Background ambience */}
          <div className="absolute inset-0 opacity-10" style={{
            backgroundImage: `url(${SCENE_IMAGES[0]})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: 'blur(20px)',
          }} />
          <div className="absolute inset-0 film-grain-overlay opacity-50" />

          <div className="relative text-center px-6 max-w-xl">
            {/* Production labels */}
            <div className="flex items-center justify-center gap-12 mb-12 opacity-40">
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '8px', letterSpacing: '0.2em', color: GOLD, textAlign: 'center' }}>
                <div>SHE BUILT THIS</div>
                <div>PRODUCTIONS</div>
              </div>
              <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '8px', letterSpacing: '0.2em', color: GOLD, textAlign: 'center' }}>
                <div>VISIONMOVIE</div>
                <div>2026</div>
              </div>
            </div>

            <p className="mb-4" style={{ fontFamily: "'DM Mono', monospace", fontSize: '10px', letterSpacing: '0.4em', color: GOLD }}>
              A FEATURE DOCUMENTARY
            </p>
            <h1 className="mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(40px, 8vw, 80px)', fontWeight: 700, letterSpacing: '0.14em', color: '#fff', lineHeight: 1.1 }}>
              SHE BUILT THIS
            </h1>
            <p className="mb-12" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '18px', fontWeight: 300, color: 'rgba(255,255,255,0.35)', lineHeight: 1.6 }}>
              A 74-second cinematic experience. Best viewed fullscreen.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                data-testid="enter-cinema-sound"
                onClick={() => enterCinema(true)}
                className="group relative overflow-hidden px-10 py-4 border transition-all duration-500 hover:bg-[rgba(200,168,64,0.1)]"
                style={{ borderColor: GOLD, fontFamily: "'DM Mono', monospace", fontSize: '11px', letterSpacing: '0.3em', color: GOLD }}
              >
                <Volume2 size={14} className="inline mr-3 mb-[2px]" />
                ENTER WITH SOUND
              </button>
              <button
                data-testid="enter-cinema-silent"
                onClick={() => enterCinema(false)}
                className="px-10 py-4 border border-[#1A1A1A]/10 transition-all duration-500 hover:border-white/30"
                style={{ fontFamily: "'DM Mono', monospace", fontSize: '11px', letterSpacing: '0.3em', color: 'rgba(255,255,255,0.3)' }}
              >
                <VolumeX size={14} className="inline mr-3 mb-[2px]" />
                SILENT MODE
              </button>
            </div>

            <p className="mt-8" style={{ fontFamily: "'DM Mono', monospace", fontSize: '9px', letterSpacing: '0.2em', color: 'rgba(255,255,255,0.15)' }}>
              CLICK ANYWHERE TO REPLAY
            </p>
          </div>
        </div>
      )}

      {/* ── MAIN TEASER ── */}
      {entered && (
        <>
          <div className="pt-[60px]">
            <CinematicTeaser soundEnabled={soundEnabled} audioRef={audioRef} />
          </div>

          {/* Sound toggle */}
          <button
            data-testid="sound-toggle"
            onClick={toggleSound}
            className="fixed bottom-6 right-6 z-50 p-3 border transition-all duration-300 hover:border-[#C8A840]"
            style={{
              borderColor: soundEnabled ? GOLD : 'rgba(255,255,255,0.15)',
              background: 'rgba(0,0,0,0.8)',
              backdropFilter: 'blur(10px)',
              color: soundEnabled ? GOLD : 'rgba(255,255,255,0.3)',
            }}
          >
            {soundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
          </button>
        </>
      )}

      {/* ── FILM INFO SECTION ── */}
      <div className="relative max-w-4xl mx-auto px-6 py-24">
        <div className="absolute inset-0 film-grain-overlay opacity-20 pointer-events-none" />

        <div className="relative text-center mb-20">
          <div className="flex items-center justify-center gap-16 mb-12 opacity-30">
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '8px', letterSpacing: '0.2em', color: GOLD, textAlign: 'center' }}>
              <div>SHE BUILT THIS</div>
              <div>PRODUCTIONS</div>
            </div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '8px', letterSpacing: '0.2em', color: GOLD, textAlign: 'center' }}>
              <div>VISIONMOVIE</div>
              <div>2026</div>
            </div>
          </div>

          <p className="mb-6" style={{ fontFamily: "'DM Mono', monospace", fontSize: '10px', letterSpacing: '0.4em', color: GOLD }}>
            COMING 2026
          </p>
          <h2 className="mb-8" style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(36px, 6vw, 72px)', fontWeight: 700, color: '#fff', letterSpacing: '0.08em', lineHeight: 1.1 }}>
            She Built This
          </h2>
          <p className="text-xl md:text-2xl max-w-2xl mx-auto" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 300, color: 'rgba(255,255,255,0.4)', lineHeight: 1.7 }}>
            A feature documentary exploring the invisible labour of women who build institutions,
            schools, companies, and movements &mdash; and never see their names on the door.
          </p>
        </div>

        <div className="w-24 h-[1px] mx-auto mb-20" style={{ background: `linear-gradient(to right, transparent, ${GOLD}, transparent)` }} />

        {/* Film details */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-20 mb-24">
          <div>
            <h3 className="mb-6" style={{ fontFamily: "'DM Mono', monospace", fontSize: '10px', letterSpacing: '0.35em', color: GOLD }}>
              THE PREMISE
            </h3>
            <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '18px', color: 'rgba(255,255,255,0.4)', lineHeight: 1.8 }}>
              After fifty years of research and $8 billion a year in global funding, zero programmes
              have been proven to increase women in leadership over time. This film asks why &mdash;
              and follows the women who built anyway.
            </p>
          </div>
          <div>
            <h3 className="mb-6" style={{ fontFamily: "'DM Mono', monospace", fontSize: '10px', letterSpacing: '0.35em', color: GOLD }}>
              THE QUESTION
            </h3>
            <p className="italic" style={{ fontFamily: "'Playfair Display', serif", fontSize: '22px', color: 'rgba(255,255,255,0.5)', lineHeight: 1.6 }}>
              &ldquo;What does it cost a person to build something that will never carry their name?&rdquo;
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-8 mb-24 text-center">
          <div>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(32px, 4vw, 48px)', fontWeight: 700, color: GOLD }}>369+</div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '9px', letterSpacing: '0.25em', color: 'rgba(255,255,255,0.25)', marginTop: '8px' }}>PROFILES DOCUMENTED</div>
          </div>
          <div>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(32px, 4vw, 48px)', fontWeight: 700, color: '#fff' }}>7</div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '9px', letterSpacing: '0.25em', color: 'rgba(255,255,255,0.25)', marginTop: '8px' }}>CONTINENTS</div>
          </div>
          <div>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(32px, 4vw, 48px)', fontWeight: 700, color: GOLD }}>0</div>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '9px', letterSpacing: '0.25em', color: 'rgba(255,255,255,0.25)', marginTop: '8px' }}>PROGRAMMES THAT WORKED</div>
          </div>
        </div>

        {/* Status */}
        <div className="border-t border-[#1A1A1A]/10 pt-12 mb-16 text-center">
          <p style={{ fontFamily: "'DM Mono', monospace", fontSize: '9px', letterSpacing: '0.3em', color: 'rgba(255,255,255,0.2)', marginBottom: '8px' }}>STATUS</p>
          <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: '20px', color: GOLD }}>In Development</p>
        </div>

        {/* CTA */}
        <div className="text-center">
          <Link
            to="/nominate"
            className="inline-flex items-center gap-2 px-8 py-4 border transition-all duration-500 hover:bg-[rgba(200,168,64,0.08)]"
            style={{ borderColor: 'rgba(200,168,64,0.3)', fontFamily: "'DM Mono', monospace", fontSize: '11px', letterSpacing: '0.25em', color: GOLD }}
            data-testid="film-nominate-cta"
          >
            NOMINATE A WOMAN <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default FilmPage;
